/**
 * Bulk-delete helper — run a per-id delete over a set of ids concurrently and
 * report how many succeeded vs failed.
 *
 * Used by the multi-select "Delete selected (N)" toolbars (Equipment,
 * Gateways, process graph). Uses `Promise.allSettled` so one failed delete
 * never aborts the rest — the caller surfaces a single summary toast
 * ("X deleted, Y failed") instead of failing the whole batch on the first
 * error. The per-id `remove` is the existing resource delete (e.g.
 * `things.remove`, `twinEntities.remove`), so this carries no endpoint
 * knowledge of its own.
 */

export interface BulkDeleteResult {
  /** ids that deleted successfully. */
  succeeded: string[];
  /** ids that failed, paired with the thrown error. */
  failed: Array<{ id: string; error: unknown }>;
}

/**
 * Delete every id via `remove`, concurrently. Resolves once all settle —
 * never rejects. `succeeded.length + failed.length === ids.length`.
 */
export async function bulkDelete(
  ids: string[],
  remove: (id: string) => Promise<unknown>,
): Promise<BulkDeleteResult> {
  const settled = await Promise.allSettled(
    ids.map((id) => remove(id).then(() => id)),
  );
  const succeeded: string[] = [];
  const failed: Array<{ id: string; error: unknown }> = [];
  settled.forEach((r, i) => {
    if (r.status === "fulfilled") {
      succeeded.push(ids[i]);
    } else {
      failed.push({ id: ids[i], error: r.reason });
    }
  });
  return { succeeded, failed };
}

/**
 * Build the summary toast text for a bulk-delete result, pluralised on the
 * `noun` (e.g. "entity"/"gateway"). Returns the message + the toast kind so
 * the caller can `toast.push(kind, message)`:
 *   - all ok      → "ok"   · "3 entities deleted"
 *   - partial     → "error"· "2 gateways deleted, 1 failed"
 *   - all failed  → "error"· "Couldn't delete 2 entities"
 */
export function bulkDeleteToast(
  result: BulkDeleteResult,
  noun: string,
  nounPlural = `${noun}s`,
): { kind: "ok" | "error"; message: string } {
  const ok = result.succeeded.length;
  const bad = result.failed.length;
  const word = (n: number) => (n === 1 ? noun : nounPlural);
  if (bad === 0) {
    return { kind: "ok", message: `${ok} ${word(ok)} deleted` };
  }
  if (ok === 0) {
    return { kind: "error", message: `Couldn't delete ${bad} ${word(bad)}` };
  }
  return {
    kind: "error",
    message: `${ok} ${word(ok)} deleted, ${bad} failed`,
  };
}
